import { Button, DialogContent, DialogTitle, TextField } from "@mui/material";
import { useState } from "react";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { except } from "@entrilia/shared/utilities/arrayHelper";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import {
  combineValidators,
  invalidResult,
  requiredValidator,
  uniqueValidator,
} from "@entrilia/shared/utilities/validators";
import FundraisingDocumentsContext from "./FundraisingDocumentsContext";

interface Props {
  editedSectionName?: string;
  onClose: () => void;
  onCreated: (sectionName: string) => void;
}

interface FormState {
  sectionName: string;
  isDirty: boolean;
  validationResult: ValidationResult;
}

const EditFileSectionDialog = ({ editedSectionName, onClose, onCreated }: Props) => {
  const { documentsState } = useDefinedContext(FundraisingDocumentsContext);

  const [formState, setFormState] = useState<FormState>(() => ({
    sectionName: editedSectionName ?? "",
    isDirty: false,
    validationResult: invalidResult(""),
  }));

  const isNew = editedSectionName === undefined;

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const otherSections = except(documentsState.orderedSections, [editedSectionName ?? ""]);

    const validateSectionName = combineValidators(
      requiredValidator,
      uniqueValidator("Such section already exists", otherSections)
    );

    setFormState({
      sectionName: e.target.value,
      isDirty: true,
      validationResult: validateSectionName(e.target.value),
    });
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>{isNew ? "New Section" : "Rename Section"}</DialogTitle>
      <DialogCloseButton onClick={onClose} />

      <DialogContent>
        <TextField
          fullWidth
          label="Name"
          variant="outlined"
          onChange={handleNameChange}
          value={formState.sectionName}
          error={formState.isDirty && !formState.validationResult.isValid}
          helperText={formState.isDirty ? formState.validationResult.error : undefined}
        />
      </DialogContent>

      <DialogButtons onCancel={onClose}>
        <Button
          variant="contained"
          onClick={() => onCreated(formState.sectionName)}
          disabled={formState.sectionName === editedSectionName || !formState.validationResult.isValid}
        >
          {isNew ? "Create" : "Rename"}
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default EditFileSectionDialog;
